/** Command boards: start one (bush bond, perimeter, search), alert who's on duty / on standby, and run it. */
import { useMemo, useState } from 'react';
import { BOARD_KINDS, BOARD_ROLES, type BoardKind, type BoardRole } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { SlippyMap, type MapMarker } from '../shared/SlippyMap.tsx';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, EmptyState, when } from './common.tsx';

const ROLE_COLOR: Record<string, string> = { perimeter: '#1f63e6', k9: '#8a4b00', drone: '#7a4fd6', available: '#2e9a5c', command: '#c0262d', traffic: '#d39a2a', staging: '#475467', medical: '#d6336c', other: '#475467' };

function AlertPrompt({ id, onDone, first }: { id: string; onDone: () => void; first?: boolean }) {
  const [scopes, setScopes] = useState<string[]>(['on_duty']);
  const [message, setMessage] = useState('');
  const a = useAction();
  const [sent, setSent] = useState<number | null>(null);
  const flip = (s: string) => setScopes(scopes.includes(s) ? scopes.filter((x) => x !== s) : [...scopes, s]);
  if (sent != null) return <Notice tone="ok">Alerted {sent} {sent === 1 ? 'person' : 'people'}. Their answers show below as they come in.</Notice>;
  return (
    <Card title={first ? 'Do you want to alert those on duty?' : 'Alert more people'}>
      <div className="row gap wrap">
        <label className="check">
          <input type="checkbox" checked={scopes.includes('on_duty')} onChange={() => flip('on_duty')} /> Everyone on duty
        </label>
        <label className="check">
          <input type="checkbox" checked={scopes.includes('standby')} onChange={() => flip('standby')} /> Everyone on standby
        </label>
      </div>
      <label>
        Message (optional)
        <input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="e.g. B/M red shirt ran east into the woods. Set up on Lowrys Rd." />
      </label>
      <p className="small muted">They get a text, an email and a message in BlueHQ, and answer Responding or Not responding.</p>
      <ErrorBox error={a.error} />
      <div className="row gap-sm">
        <button className="btn primary" disabled={a.busy || !scopes.length} onClick={() => a.run(async () => (setSent((await api.post<any>(`/api/command/${id}/alert`, { scopes, message: message || null })).alerted), onDone()))}>
          Yes, alert them
        </button>
        {first && (
          <button className="btn" onClick={() => setSent(0)}>
            Not now
          </button>
        )}
      </div>
    </Card>
  );
}

export function NewCommandBoard() {
  const { navigate } = useRouter();
  const [kind, setKind] = useState<BoardKind>('bush_bond');
  const [location, setLocation] = useState('');
  const [pt, setPt] = useState<{ lat: number; lng: number } | null>(null);
  const [details, setDetails] = useState('');
  const a = useAction();
  const here = () =>
    navigator.geolocation?.getCurrentPosition(
      (p) => {
        setPt({ lat: p.coords.latitude, lng: p.coords.longitude });
        if (!location) setLocation(`Near ${p.coords.latitude.toFixed(5)}, ${p.coords.longitude.toFixed(5)}`);
      },
      () => undefined,
      { enableHighAccuracy: true, timeout: 8000 },
    );
  return (
    <div className="ops stack">
      <PageHeader crumb="Command boards" title="Start a command board" />
      <Card title="What is it?">
        <div className="seg-ctl wrap" role="radiogroup" aria-label="Kind">
          {(Object.keys(BOARD_KINDS) as BoardKind[]).map((k) => (
            <button key={k} type="button" role="radio" aria-checked={kind === k} className={kind === k ? 'on' : ''} onClick={() => setKind(k)}>
              {BOARD_KINDS[k]}
            </button>
          ))}
        </div>
      </Card>
      <Card title="Where?">
        <div className="row gap-sm wrap" style={{ alignItems: 'end' }}>
          <label style={{ flex: 1, minWidth: 240 }}>
            Location
            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Hwy 9 at Lowrys Rd" />
          </label>
          <button type="button" className="btn" onClick={here}>
            📍 Use my location
          </button>
        </div>
        <div className="cb-pick">
          <SlippyMap height={280} markers={pt ? [{ id: 'pt', ...pt, color: '#c0262d', title: 'Command board location' }] : []} fitKey={pt ? `${pt.lat},${pt.lng}` : 'none'} onMapClick={(p) => setPt(p)} crosshair ariaLabel="Tap the map to drop the location" />
          <p className="small muted">Tap the map to put the pin where it is.</p>
        </div>
        <label>
          Details (optional)
          <textarea rows={2} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Description, direction of travel, hazards" />
        </label>
      </Card>
      <ErrorBox error={a.error} />
      <div>
        <button className="btn primary" disabled={a.busy || !location.trim()} onClick={() => a.run(async () => navigate(at(`/command/${(await api.post<any>('/api/command', { kind, location, lat: pt?.lat ?? null, lng: pt?.lng ?? null, details: details || null })).id}?new=1`), true))}>
          Start command board
        </button>
      </div>
    </div>
  );
}

export function CommandBoardsPage() {
  const { navigate } = useRouter();
  const [all, setAll] = useState(false);
  const r = useLoad(() => api.get<any>(`/api/command${all ? '?status=all' : ''}`), [all]);
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Patrol"
        title="Command boards"
        sub="Bush bonds, perimeters and searches: who’s responding and what each is doing."
        actions={
          r.data?.canStart && (
            <button className="btn primary cb-start" onClick={() => navigate(at('/command/new'))}>
              Start command board
            </button>
          )
        }
      />
      <label className="check">
        <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Show closed boards
      </label>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.boards.length ? (
        <EmptyState icon="check" title="No active command boards" />
      ) : (
        <div className="card table-wrap">
          <table className="table clickable">
            <thead>
              <tr>
                <th>Board</th>
                <th>Where</th>
                <th>Responding</th>
                <th>Started</th>
              </tr>
            </thead>
            <tbody>
              {r.data.boards.map((b: any) => (
                <tr key={b.id} onClick={() => navigate(at(`/command/${b.id}`))}>
                  <td>
                    <strong>{BOARD_KINDS[b.kind as BoardKind] ?? b.kind}</strong>
                    <div className="small muted mono">{b.number}</div>
                    {b.status === 'closed' && <Badge>Closed</Badge>}
                    {b.status === 'active' && b.alerted && !b.my_response && <Badge tone="bad">Answer needed</Badge>}
                  </td>
                  <td>{b.location}</td>
                  <td>
                    {b.responding} responding · {b.on_scene} on scene
                  </td>
                  <td className="small">
                    {when(b.started_at)} · {b.started_by_name}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function CommandBoardPage({ id }: { id: string }) {
  const r = useLoad(() => api.get<any>(`/api/command/${id}`), [id]);
  const a = useAction();
  const [post, setPost] = useState('');
  const [note, setNote] = useState('');
  const isNew = new URLSearchParams(location.search).get('new') === '1';
  const markers = useMemo<MapMarker[]>(() => {
    if (!r.data) return [];
    const b = r.data.board;
    const m: MapMarker[] = [];
    if (b.lat != null) m.push({ id: 'board', lat: b.lat, lng: b.lng, color: '#c0262d', label: '!', title: b.location });
    for (const p of r.data.people) if (p.lat != null && p.response === 'responding') m.push({ id: p.user_id, lat: p.lat, lng: p.lng, color: ROLE_COLOR[p.role] ?? '#1f63e6', label: p.call_number ?? undefined, title: `${p.name}${p.role ? ` · ${BOARD_ROLES[p.role as BoardRole]}` : ' · responding'}` });
    return m;
  }, [r.data]);
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { board: b, people, me, canRun, log } = r.data;
  const open = b.status === 'active';
  const act = (path: string, body: unknown) => a.run(async () => (await api.post(`/api/command/${id}/${path}`, body), r.reload()));
  const onScene = people.filter((p: any) => p.response === 'responding' && p.role);
  const enRoute = people.filter((p: any) => p.response === 'responding' && !p.role);
  const notComing = people.filter((p: any) => p.response === 'not_responding');
  const noAnswer = people.filter((p: any) => p.alerted && !p.response);
  const byRole = (Object.keys(BOARD_ROLES) as BoardRole[]).map((k) => ({ k, list: onScene.filter((p: any) => p.role === k) })).filter((x) => x.list.length);
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Command boards"
        title={`${BOARD_KINDS[b.kind as BoardKind] ?? b.kind} · ${b.location}`}
        sub={`${b.number} · started ${when(b.started_at)} by ${b.started_by_name}${b.details ? ` · ${b.details}` : ''}`}
        actions={
          canRun &&
          open && (
            <button className="btn" disabled={a.busy} onClick={() => { const n = window.prompt('Close this board? Note (optional):'); if (n !== null) act('close', { note: n || null }); }}>
              Close board
            </button>
          )
        }
      />
      {!open && <Notice tone="info">Closed {when(b.closed_at)}{b.close_note ? ` — ${b.close_note}` : ''}.</Notice>}
      <ErrorBox error={a.error} />
      {open && canRun && (isNew || !b.alerted_at) && <AlertPrompt id={id} first={!b.alerted_at} onDone={r.reload} />}
      {open && (
        <Card title="You">
          {me?.response !== 'responding' ? (
            <div className="row gap-sm wrap">
              <button className="btn primary cb-big" disabled={a.busy} onClick={() => act('respond', { response: 'responding' })}>
                Responding
              </button>
              <button className={`btn cb-big${me?.response === 'not_responding' ? ' on' : ''}`} disabled={a.busy} onClick={() => act('respond', { response: 'not_responding' })}>
                Not responding
              </button>
            </div>
          ) : (
            <div className="stack sm">
              <span className="small muted">{me.role ? `You: ${BOARD_ROLES[me.role as BoardRole]}${me.post ? ` — ${me.post}` : ''}` : 'Responding. When you get there, tap what you’re doing:'}</span>
              <div className="cb-roles">
                {(Object.keys(BOARD_ROLES) as BoardRole[]).map((k) => (
                  <button key={k} type="button" className={`btn cb-role${me.role === k ? ' on' : ''}`} disabled={a.busy} onClick={() => act('role', { role: k, post: post || me.post || null })}>
                    {k === 'available' ? 'I’m available for assignment' : k === 'perimeter' ? 'I’m on perimeter' : k === 'k9' ? 'I’m running with K9' : k === 'drone' ? 'I’m in the drone' : BOARD_ROLES[k]}
                  </button>
                ))}
              </div>
              <div className="row gap-sm wrap" style={{ alignItems: 'end' }}>
                <label style={{ flex: 1, minWidth: 200 }}>
                  Where you are / your post
                  <input value={post} onChange={(e) => setPost(e.target.value)} placeholder={me.post ?? 'e.g. NE corner, Lowrys Rd at the tracks'} />
                </label>
                <button className="btn link small" onClick={() => act('respond', { response: 'not_responding' })}>
                  I can’t make it after all
                </button>
              </div>
            </div>
          )}
        </Card>
      )}
      {markers.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <SlippyMap height={320} markers={markers} fitKey={markers.map((m) => m.id).join(',')} ariaLabel="Command board map" />
        </div>
      )}
      <div className="grid-2-cards">
        <Card title={`On scene (${onScene.length})`}>
          {!byRole.length ? (
            <p className="muted small">No one on scene yet.</p>
          ) : (
            byRole.map(({ k, list }) => (
              <div key={k} className="cb-group">
                <h4>
                  <i style={{ background: ROLE_COLOR[k] }} aria-hidden="true" /> {BOARD_ROLES[k]} ({list.length})
                </h4>
                <ul className="plain">
                  {list.map((p: any) => (
                    <li key={p.user_id}>
                      <strong>{p.name}</strong>
                      {p.call_number ? ` (${p.call_number})` : ''}
                      {p.post && <span className="small"> — {p.post}</span>}
                      {p.resources.length > 0 && <div className="small muted">{p.resources.join(' · ')}</div>}
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </Card>
        <Card title="Responses">
          <div className="cb-group">
            <h4>Responding, not on scene yet ({enRoute.length})</h4>
            <p className="small">{enRoute.map((p: any) => p.name).join(', ') || '—'}</p>
          </div>
          <div className="cb-group">
            <h4>No answer yet ({noAnswer.length})</h4>
            <p className="small">{noAnswer.map((p: any) => `${p.name}${p.alerted_as === 'standby' ? ' (standby)' : ''}`).join(', ') || '—'}</p>
          </div>
          <div className="cb-group">
            <h4>Not responding ({notComing.length})</h4>
            <p className="small muted">{notComing.map((p: any) => p.name).join(', ') || '—'}</p>
          </div>
          {open && canRun && b.alerted_at && (
            <details>
              <summary className="small">Alert more people</summary>
              <AlertPrompt id={id} onDone={r.reload} />
            </details>
          )}
        </Card>
      </div>
      <Card title="Log">
        {open && (
          <div className="row gap-sm">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an update (e.g. K9 has a track heading north)" aria-label="Update" style={{ flex: 1 }} />
            <button className="btn" disabled={a.busy || !note.trim()} onClick={() => (act('note', { text: note }), setNote(''))}>
              Add
            </button>
          </div>
        )}
        <ul className="plain small cb-log">
          {log.map((l: any, i: number) => (
            <li key={i}>
              <span className="muted">{new Date(l.at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span> {l.text}
              {l.by_name && !l.text.startsWith(l.by_name) ? <span className="muted"> · {l.by_name}</span> : null}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
